import { onBeforeUnmount, onMounted, watch, type Ref } from 'vue'

/**
 * Wire the brand's Liquid Glass (sixth signature element) onto an element ref.
 *
 * Canon: `knowledge/brand/tokens.md` -> "Liquid glass". The module itself is
 * vendored verbatim at `../utils/liquid-glass.js` from `.claude/skills/liquid-glass/`.
 * This composable owns only the Vue lifecycle around it.
 *
 * **The module is loaded with a dynamic `import()`, deliberately.** It touches
 * `navigator` and `CSS.supports` at module-evaluation time, so a static import
 * would throw the moment anything outside a browser pulls in `@claimshield/shared`
 * (type-check harnesses, tests, any future SSR). Lazy-loading also keeps the
 * SVG-filter machinery out of the entry bundle for the many views that never use it.
 *
 * For the same reason this is NOT re-exported from the package barrel.
 *
 * Refraction is Chromium-only; Safari and Firefox degrade to a frosted blur
 * inside the module. Never let refraction carry meaning.
 */

export interface LiquidGlassOptions {
  /** Displacement strength; negative = magnifying bulge. Subtle -60, default -112. */
  scale?: number
  /** Per-channel stagger for the prism fringe; 0 disables it. */
  chroma?: number
  /** Neutral inset as a fraction of the smaller side. */
  border?: number
  /** Edge-curvature softness (px) of the map's gray inset. */
  mapBlur?: number
  /** Backdrop blur (px) behind the glass interior. */
  blur?: number
  /** Backdrop saturation boost. */
  saturate?: number
  /** Corner radius override (px). Defaults to the element's own border-radius. */
  radius?: number
  /** Frosted blur (px) where refraction is unsupported. */
  fallbackBlur?: number
}

interface GlassHandle {
  supported: boolean
  refresh: () => void
  destroy: () => void
}

/**
 * Canon tunings (tokens.md): cards `-112/6` · nav bars `-64/4 + border .14, mapBlur 8`
 * · pills `-60/4`. A short floating bar is nav-bar class, not card class — the
 * refraction band eats a larger share of a short element's height, so the strong
 * card tuning smears its interior.
 */
export const GLASS_TUNING = {
  card: { scale: -112, chroma: 6 },
  navBar: { scale: -64, chroma: 4, border: 0.14, mapBlur: 8 },
  pill: { scale: -60, chroma: 4 },
} as const satisfies Record<string, LiquidGlassOptions>

export function useLiquidGlass(
  target: Ref<HTMLElement | null | undefined>,
  options: LiquidGlassOptions = {},
) {
  let handle: GlassHandle | null = null
  let disposed = false

  async function attach(el: HTMLElement) {
    const { liquidGlass } = await import('../utils/liquid-glass.js')
    // The ref can change or the component unmount while the chunk is in flight.
    if (disposed || target.value !== el) return
    handle = liquidGlass(el, options) as GlassHandle
  }

  function detach() {
    handle?.destroy()
    handle = null
  }

  onMounted(() => {
    if (target.value) void attach(target.value)
  })

  // The element this decorates is usually behind a `v-if` (a toast, a prompt),
  // so it does not exist at mount and appears later — and can be replaced.
  watch(target, (el, prev) => {
    if (prev && prev !== el) detach()
    if (el && !handle) void attach(el)
  })

  onBeforeUnmount(() => {
    disposed = true
    detach()
  })

  return {
    /** Re-measure after a size change the ResizeObserver cannot see. */
    refresh: () => handle?.refresh(),
  }
}
